<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PersonPage                                            |
| Section: views/people                                            |
| Purpose: Person detail route with bio, family, and media modules.|
+------------------------------------------------------------------+
-->
<template>

    <PersonPrerenderSnapshot v-if="person && isPrerenderSnapshot" :person="person" :locale="locale"
        :localePath="localePath" />

    <!-- <div v-if="loading"> -->
    <!-- You can either:
         a) show big generic skeleton blocks here
         b) or delegate to component-level skeletons -->
    <!-- <SkeletonPersonHeader /> -->
    <!-- </div> -->

    <!-- <div v-else> -->

    <section class="print-dossier" :class="{ 'print-dossier--screen': isPrintView }" v-if="person && !isPrerenderSnapshot"
        :data-nosnippet="!isPrintView ? '' : null">
        <header class="print-header">
            <p class="print-eyebrow">{{ $t('personPage.print.eyebrow') }}</p>
            <h1>{{ personDisplayName }}</h1>
            <p class="print-subtitle">{{ personLifecycleRange }}</p>
        </header>

        <section class="print-section" v-if="printCoreFacts.length > 0">
            <h2>{{ $t('personPage.print.sections.coreFacts') }}</h2>
            <table class="print-table">
                <tbody>
                    <tr v-for="row in printCoreFacts" :key="`fact-${row.label}`">
                        <th>{{ row.label }}</th>
                        <td>{{ row.value }}</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section class="print-section" v-if="printEventRows.length > 0">
            <h2>{{ $t('personPage.print.sections.lifecycle') }}</h2>
            <table class="print-table print-table--timeline">
                <thead>
                    <tr>
                        <th>{{ $t('personPage.print.table.date') }}</th>
                        <th>{{ $t('personPage.print.table.event') }}</th>
                        <th>{{ $t('personPage.print.table.place') }}</th>
                        <th>{{ $t('personPage.print.table.details') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in printEventRows" :key="`event-${index}`">
                        <td>{{ row.date || '-' }}</td>
                        <td>{{ row.event }}</td>
                        <td>{{ row.place || '-' }}</td>
                        <td>{{ row.details || '-' }}</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section class="print-section" v-if="printParentRows.length > 0">
            <h2>{{ $t('personPage.print.sections.parents') }}</h2>
            <table class="print-table">
                <thead>
                    <tr>
                        <th>{{ $t('personPage.print.table.relation') }}</th>
                        <th>{{ $t('personPage.print.table.name') }}</th>
                        <th>{{ $t('personPage.print.table.lifeYears') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(parent, index) in printParentRows" :key="`parent-${index}`">
                        <td>{{ parent.relation }}</td>
                        <td>{{ parent.name }}</td>
                        <td>{{ parent.years || '-' }}</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section class="print-section" v-if="printFamilyRows.length > 0">
            <h2>{{ $t('personPage.print.sections.family') }}</h2>
            <table class="print-table">
                <thead>
                    <tr>
                        <th>{{ $t('personPage.print.table.partner') }}</th>
                        <th>{{ $t('personPage.print.table.marriage') }}</th>
                        <th>{{ $t('personPage.print.table.location') }}</th>
                        <th>{{ $t('personPage.print.table.children') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(familyRow, index) in printFamilyRows" :key="`family-${index}`">
                        <td>{{ familyRow.spouse }}<span v-if="familyRow.spouseYears"> ({{ familyRow.spouseYears
                        }})</span></td>
                        <td>{{ familyRow.marriageDate }}</td>
                        <td>{{ familyRow.marriagePlace }}</td>
                        <td>{{ familyRow.children }}</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section class="print-section" v-if="printDocumentRows.length > 0">
            <h2>{{ $t('personPage.print.sections.documents') }}</h2>
            <table class="print-table">
                <thead>
                    <tr>
                        <th>{{ $t('personPage.print.table.number') }}</th>
                        <th>{{ $t('personPage.print.table.description') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(doc, index) in printDocumentRows" :key="`doc-${index}`">
                        <td>{{ index + 1 }}</td>
                        <td>{{ doc }}</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <section class="print-section" v-if="printNotesRows.length > 0">
            <h2>{{ $t('personPage.print.sections.notes') }}</h2>
            <table class="print-table">
                <thead>
                    <tr>
                        <th>{{ $t('personPage.print.table.number') }}</th>
                        <th>{{ $t('personPage.print.table.content') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(note, index) in printNotesRows" :key="`note-${index}`">
                        <td>{{ index + 1 }}</td>
                        <td>{{ note }}</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <footer class="print-footer">
            <p>{{ $t('personPage.print.footer.printedOn', { date: printGeneratedDate }) }}</p>
            <p v-if="printSourceUrl">{{ $t('personPage.print.footer.source', { url: printSourceUrl }) }}</p>
            <p>{{ $t('personPage.print.footer.credit') }}</p>
        </footer>
    </section>

    <div class="person-page-screen" v-if="!isPrintView && !isPrerenderSnapshot" :key="$route.params.id">
        <PersonSectionNavigator v-if="sectionNavigationReady && navigablePersonSections.length > 1"
            :sections="navigablePersonSections" :ariaLabel="$t('personPage.sectionNavigation.ariaLabel')" />

        <PersonHeader @open-overlay="openMediaOverlay" @loaded="handlePersonHeaderLoaded" />

        <div class="dna-notifications-container">
            <DNAMatchNotification v-if="currentDnaMatch"
                :key="`${currentDnaMatch.personID}-${currentDnaMatch.category}`" :matchData="currentDnaMatch"
                :queuePosition="currentDnaMatchIndex" :totalCount="totalDnaMatches" @closed="advanceDnaMatchQueue" />
        </div>

        <div id="bioDataSection" v-if="hasLifeEventData">
            <!-- First BioDataCard with dynamic title, label, and random photo -->
            <BioDataCard v-if="person && person.personID" :class="{ 'BioDataCard_guest': !authToken }" :reverse="false"
                :bioDataGroups="bioDataGroupsPart1" :title="bioDataCardTitle1" :label="bioDataCardEyebrow1"
                :photoSrc="selectedPhoto1" :showPhotosButton="photos.length > 2" :gender="computedGender"
                :personID="person.personID" :notes="notes" :openMediaOverlay="openMediaOverlay" :showEventMap="false"
                @open-gallery-overlay="openPhotoGalleryOverlay" />
        </div>

        <div v-if="!hasLifeEventData" class="buffer"></div>

        <div id="parentSection">
            <ParentSection v-if="person && person.personID" :parents="parents" :dynamicLinkLabel="dynamicLinkLabel"
                :personID="person.personID" :person="person" />
        </div>

        <div id="eventSection" v-if="showEventSection">
            <!-- Second BioDataCard with dynamic title, label, and different random photo -->
            <BioDataCard v-if="person && person.personID" :class="{ 'BioDataCard_guest': !authToken }" :reverse="true"
                :bioDataGroups="bioDataGroupsPart2" :title="bioDataCardTitle2" :label="bioDataCardLabel2"
                :photoSrc="selectedPhoto2" :showPhotosButton="photos.length > 2" :gender="person ? person.sex : ''"
                :notes="notes" :openMediaOverlay="openMediaOverlay" @open-gallery-overlay="openPhotoGalleryOverlay"
                :showEventMap="true" :personID="person.personID" :marriageData="marriageDataArray"
                :generalNotes="generalNotes" />
        </div>

        <!-- <div v-if="story" class="StoryCard_wrapper wrapper">
        <StoryCard :titleGroupTitle="story.title" :description="story.summary"
            :imageSrc="story.image || defaultImageSrc" :buttonLabel="'Lees meer'"
            :buttonHref="`/verhalen/${storySlug}`" />
    </div>

    <div v-if="familyStories.length" class="StoryCard_wrapper wrapper">
        <StoryCard v-for="(famStory, i) in familyStories" :key="i"
            :titleGroupTitle="famStory.title" :description="famStory.summary"
            :imageSrc="famStory.image || defaultImageSrc" buttonLabel="Lees meer"
            :buttonHref="`/verhalen/${famStory.slug}`"
            :reverse="(!!story ? i + 1 : i) % 2 !== 0" />
    </div> -->

        <div id="familySection">
            <SpouseSection v-if="families.length > 0" :families="families" :familyTitle="familyTitle"
                :personID="person.personID" :person="person" :openMediaOverlay="openMediaOverlay"
                :familyInfo="familyInfo" :showFamilySectionChildCards="showFamilySectionChildCards" />
        </div>

        <div id="documentationSection" :style="documentationSectionStyle">
            <div class="MediaCardGroup_container wrapper"
                :class="{ 'MediaCardGroup_container--after-events-no-descendants': !hasDescendantGraph && showEventSection }"
                v-if="media.length > 0">
                <div class="MediaCardGroup_wrapper wrapper">
                    <div class="DocumentationSection_heading">
                        <TitleGroup :label="$t('personPage.documents.sectionLabel')"
                            :title="$t('personPage.documents.sectionTitle')" :isWhiteText="false" :showLink="false" />
                        <button type="button" class="DocumentationSection_view_toggle"
                            :aria-pressed="String(documentationView === 'list')"
                            :aria-controls="documentationPanelId" @click="toggleDocumentationView">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" aria-hidden="true">
                                <path d="M8 6h13M8 12h13M8 18h13" />
                                <path d="M3 6h.01M3 12h.01M3 18h.01" />
                            </svg>
                            {{ documentationView === 'list'
                                ? $t('personPage.documents.cardView')
                                : $t('personPage.documents.listView') }}
                        </button>
                    </div>
                    <div v-if="documentationView === 'cards'" :id="documentationPanelId" class="MediaCards">
                        <MediaCard v-for="(mediaItem, index) in media" :key="index" :label="mediaItem.description"
                            :thumbnail="authToken || isPublicMedia(mediaItem.description) ? getThumbnailPath(mediaItem.thumbpath) : 'path/to/guest-thumbnail.jpg'"
                            :url="authToken || isPublicMedia(mediaItem.description) ? getFilePath(mediaItem.path) : localePath('/login')"
                            :class="{ 'guest-media-card': !authToken && !isPublicMedia(mediaItem.description) }"
                            @click="activateDocument(mediaItem, index)" />
                    </div>
                    <ul v-else :id="documentationPanelId" class="DocumentationList">
                        <li v-for="item in documentListItems" :key="item.key" class="DocumentationList_item">
                            <button type="button" class="DocumentationList_button"
                                :class="{ 'DocumentationList_button--guest': !item.isAccessible }"
                                @click="activateDocument(item.mediaItem, item.index)">
                                <span class="DocumentationList_thumbnail">
                                    <img v-if="item.isAccessible" class="DocumentationList_thumbnail_image"
                                        :src="item.thumbnail" alt="" />
                                    <img v-else class="DocumentationList_lock" src="@/assets/icons/lock.svg" alt="" />
                                </span>
                                <span class="DocumentationList_copy">
                                    <strong class="DocumentationList_title">{{ item.title }}</strong>
                                    <span v-if="item.source" class="DocumentationList_source">
                                        {{ $t('personPage.documents.source', { source: item.source }) }}
                                    </span>
                                    <span v-if="item.updatedLabel" class="DocumentationList_updated">
                                        {{ item.updatedLabel }}
                                    </span>
                                </span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div v-if="branchLoading" class="person-section-loading wrapper" role="status"
            :aria-label="$t('personPage.loading.kinship')">
            <div class="skeleton person-section-loading_title"></div>
            <div class="skeleton person-section-loading_card person-section-loading_card--short"></div>
        </div>

        <div id="kinshipSection" v-else :class="kinshipSectionClasses">
            <RelationshipResearchStatus v-if="branchLoadError" :loadError="true" />
            <RelationshipResearchStatus v-else-if="branches.length" :branches="branches" />
            <KinshipCalculator v-else-if="person && person.personID" :personID="person.personID" :person="person" />
        </div>

        <div v-if="hasRelevantStories" class="post_suggestions">
            <div class="post_suggestions_container">
                <TitleGroup :label="$t('personPage.stories.sectionLabel')"
                    :title="$t('personPage.stories.sectionTitle')" :isWhiteText="true" />
                <div class="post_suggestions_wrapper">
                    <SuggestedBlogPost v-if="story" :label="story.title" :thumbnail="story.image || defaultImageSrc"
                        :url="localePath(`/verhalen/${storySlug}`)" />
                    <SuggestedBlogPost v-for="(famStory) in familyStories" :key="famStory.slug" :label="famStory.title"
                        :thumbnail="famStory.image || defaultImageSrc"
                        :url="localePath(`/verhalen/${famStory.slug}`)" />
                </div>
            </div>
        </div>

        <MediaOverlay v-if="showOverlay" :isVisible="showOverlay" :mediaUrl="overlayMedia?.url"
            :mediaType="overlayMedia?.type" :mediaKind="overlayMedia?.kind" :mediaTitle="overlayMedia?.title"
            :mediaOwner="overlayMedia?.owner"
            :mediaList="overlayMediaArray" :currentMediaIndex="currentMediaIndex"
            :linkedPersonIDs="overlayMedia?.linkedPersonIDs"
            @close="closeMediaOverlay" @navigate="handleMediaNavigation" />

        <PhotoGalleryOverlay v-if="showPhotoGalleryOverlay" :isVisible="showPhotoGalleryOverlay"
            @close="closePhotoGalleryOverlay" :photos="photos" />

    </div>

    <!-- </div> -->

</template>

<script>
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import TitleGroup from '@/atoms/TitleGroup.vue';
import BioDataCard from '@/components/BioDataCard.vue';
import PersonHeader from '@/components/PersonHeader.vue';
import MediaCard from '@/atoms/MediaCard.vue';
import ParentSection from '@/components/Person/ParentSection.vue';
import PersonSectionNavigator from '@/components/Person/PersonSectionNavigator.vue';
import RelationshipResearchStatus from '@/components/Person/RelationshipResearchStatus.vue';
import SpouseSection from '@/components/Person/FamilySection.vue';
import KinshipCalculator from '@/components/KinshipCalculator.vue';
import MediaOverlay from '@/components/MediaOverlay.vue';
import PhotoGalleryOverlay from '@/components/PhotoGalleryOverlay.vue';
// import StoryCard from '@/components/StoryCard.vue';
import { getPersonById, getPublicPrerenderPerson, getPersonDefaultPhoto, getPersonMedia, getPersonEvents, getPersonPhotos, getFamilyDetails, getPersonBranches, getParents, getEventMedia } from '@/api/personService.js';
import { formatDate, extractSortableDate, formatJavaneseDateWithWeton, formatYears } from '@/utils/dateUtils.js'; // Import the utility function
import { getGenderBasedText } from '@/utils/textUtils';
import { getDefaultPhoto } from '@/utils/photoUtils';
import { getDocumentPath, getDocumentThumbnailPath, getPhotoPath as getPhotoPathUtil, normalizeLinkedPersonIDs, normalizeOverlayMedia } from '@/utils/mediaUtils';
import { debugLog, debugWarn } from '@/utils/logger';
// import { getPostBySlug } from '@/api/blogService';
import postsData from '@/data/getBlogPosts/posts.json'
import { getNotes } from '@/api/personService';
import publicMediaConfig from '@/data/publicMediaConfig.json';
import { applyPersonSeo, applyBreadcrumbSeo } from '@/utils/seoUtils';
import { storyByFamily } from '@/data/familyStoriesConfig.js';
import { extractPersonIDsFromContent } from '@/utils/storyUtils';
import inDeSchaduwVan4Mei from '@/data/getBlogPost/in-de-schaduw-van-4-mei.json';
import SuggestedBlogPost from '@/components/SuggestedBlogPost.vue';
import DNAMatchNotification from '@/components/DNAMatchNotification.vue';
import PersonPrerenderSnapshot from '@/components/prerender/PersonPrerenderSnapshot.vue';
import { fetchDnaMatches } from '@/api/apiService';
import { buildPersonBiography, personDisplayName, personLifespan } from '@/utils/personSeoContent';
import { isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';
// import SkeletonPersonHeader from '@/components/Skeletons/SkeletonPersonHeader.vue';

export default {
    components: {
        ParentSection,
        PersonSectionNavigator,
        RelationshipResearchStatus,
        SpouseSection,
        BioDataCard,
        TitleGroup,
        PersonHeader,
        MediaCard,
        KinshipCalculator,
        MediaOverlay,
        PhotoGalleryOverlay,
        // StoryCard,
        SuggestedBlogPost,
        DNAMatchNotification,
        PersonPrerenderSnapshot,
        // SkeletonPersonHeader,
    },
    setup() {
        // Injecting properties from App.vue using the useAuth composable
        const { authToken, username, logout } = useAuth();
        const { localePath, locale } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
            locale,
        };
    },
    data() {
        return {
            person: null, // To store the person data
            families: [],
            familyInfo: [],
            showFamilySectionChildCards: false,
            familyTitle: '',
            parents: [],
            media: [], // To store the media data
            documentationView: 'list',
            photos: [], // To store photo media data
            showOverlay: false,
            showPhotoGalleryOverlay: false, // New state for the photo gallery overlay
            selectedPhoto1: '', // To store the selected background photo for the first BioDataCard
            selectedPhoto2: '', // To store the selected background photo for the second BioDataCard
            defaultPhotos: [
                require('@/assets/sejarah_thumann1080x781.jpg'),
            ],
            overlayMedia: null,
            bioDataGroupsPart1: [], // Initialize as an empty array
            bioDataGroupsPart2: [],
            marriageDataArray: [], // New property for multiple marriages
            story: null,  // To store the story associated with the person
            storySlug: null, // Define storySlug here
            storyMapping: {  // Define the mapping of personIDs to story slugs
                I15: 'francis-duncan-jacobsz',
                I2: 'angeline-amaly-jacobsz',
                I16: 'alexandrien-frederique-westhoff',
                I11: 'antoine-thumann',
                I9: 'anton-herman-gerard-hendrik-thumann',
                I1: 'charles-jozef-thumann',
                I10: 'josina-akolo',
                // Add more mappings as needed
            },
            overlayMediaArray: [], // Stores the active document set for the overlay
            currentMediaIndex: 0,
            notes: [], // Add this to store notes
            loading: true,
            familyStories: [],
            branches: [],
            branchLoadError: false,
            branchLoading: true,
            dnaMatches: [], // Remaining DNA matches waiting in the queue
            currentDnaMatch: null,
            totalDnaMatches: 0,
            printParents: [],
        };
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler(newPersonID) {
                if (newPersonID) {
                    this.$options.loadPersonData.call(this, newPersonID);
                } else {
                    console.error('No valid personID found.');
                }
            }
        },
        '$route.params.locale'(newLocale, oldLocale) {
            const resolvedNewLocale = newLocale || 'nl';
            const resolvedOldLocale = oldLocale || 'nl';

            if (resolvedNewLocale === resolvedOldLocale) return;

            // The router reuses PersonPage when only the locale prefix changes.
            // Reload locale-derived arrays whose translated strings were created
            // during the original person request rather than during rendering.
            const personID = this.$route.params.id;
            if (personID) {
                this.$options.loadPersonData.call(this, personID);
            }
        }
    },
    async loadPersonData(personID) {

        if (!personID) {
            console.error('personID is undefined');
            return; // Exit early if no personID
        }

        try {
            this.loading = true;
            document.body.classList.add('no-scroll');
            this.resetPersonScopedState();

            // Fetch person data
            // const personData = await getPersonById(personID);

            try {
                this.loading = true
                document.body.classList.add('no-scroll')

                const personData = this.isPrerenderSnapshot
                    ? await getPublicPrerenderPerson(personID)
                    : await getPersonById(personID)

                if (!personData) {
                    console.error('Person data is null or undefined')
                    return
                }

                this.person = personData

                if (isPrivateLivingPerson(this.person, this.authToken)) {
                    this.redirectToLogin()
                    return
                }

                const biography = this.isPrerenderSnapshot
                    ? buildPersonBiography(this.person, this.locale, this.$t)
                    : [];
                const description = this.isPrerenderSnapshot
                    ? [
                        `${personDisplayName(this.person)} - ${personLifespan(this.person, this.locale)}.`,
                        ...biography,
                    ].join(' ')
                    : undefined;

                document.documentElement.lang = this.locale;
                applyPersonSeo(this.person, {
                    personId: personID,
                    currentLocale: this.locale,
                    description,
                    includeTwitter: true,
                    includeJsonLd: true,
                    includeHreflangs: true
                })

                // Add breadcrumb schema for person page
                const homeUrl = this.locale === 'nl'
                    ? 'https://sejarah-thumann.online/'
                    : `https://sejarah-thumann.online/${this.locale}/`;
                const personUrl = this.locale === 'nl'
                    ? `https://sejarah-thumann.online/person/${personID}`
                    : `https://sejarah-thumann.online/${this.locale}/person/${personID}`;
                const personName = [this.person.firstname, this.person.lastname]
                    .map(part => (typeof part === 'string' ? part.trim() : ''))
                    .filter(Boolean)
                    .join(' ') || 'Persoon';

                applyBreadcrumbSeo([
                    { name: 'Home', url: homeUrl },
                    { name: personName, url: personUrl }
                ])

                if (this.isPrerenderSnapshot) {
                    return
                }
            } catch (err) {
                console.error('Error initializing person data:', err)
                if (this.shouldRedirectToLoginForPersonError(err)) {
                    this.redirectToLogin();
                }
                return
            }

            // Check for DNA match with supported tester categories
            try {
                const dnaResponse = await fetchDnaMatches();
                const matches = (Array.isArray(dnaResponse?.matches) ? dnaResponse.matches : [])
                    .filter(match => match.personID === personID);

                this.totalDnaMatches = matches.length;
                this.currentDnaMatch = matches[0] || null;
                this.dnaMatches = matches.slice(1);

                if (matches.length > 0) {
                    debugLog(`Found ${matches.length} DNA match(es) for this person:`, matches);
                }
            } catch (error) {
                console.error('Error checking DNA matches:', error);
            }

            try {
                // 1) compute which slug is the “main” story for this person
                this.storySlug = this.storyMapping[personID] || null

                // 2) exacte match op variaties binnen families (via storyByFamily)
                const rawLast = (this.person.lastname || '').trim().toLowerCase()
                this.familyStorySlugs = Object.entries(storyByFamily)
                    .filter(([, variants]) => variants.includes(rawLast))
                    .map(([slug]) => slug)

                // 3) pull your posts JSON into a flat array
                const allPosts = postsData.data

                // 4) look up the “main” story object by slug (or null)
                this.story = this.storySlug
                    ? allPosts.find(post => post.slug === this.storySlug) || null
                    : null

                // 5) collect **all** family‑stories whose slugs you found
                this.familyStories = allPosts.filter(post =>
                    this.familyStorySlugs.includes(post.slug)
                )

                // 6) Extra verhalen op basis van personID uit blogpost content
                const linkedPersonIDs = extractPersonIDsFromContent(inDeSchaduwVan4Mei.content);
                if (linkedPersonIDs.includes(personID)) {
                    const extraPost = allPosts.find(post => post.slug === 'in-de-schaduw-van-4-mei');
                    if (extraPost) {
                        // Voeg toe zonder duplicaat
                        const alreadyExists = this.familyStories.some(existing => existing.slug === extraPost.slug);
                        if (!alreadyExists) {
                            this.familyStories.push(extraPost);
                        }
                    }
                }

                // Fetch Notes
                const notesResponse = await getNotes(personID);
                if (notesResponse) {
                    this.notes = notesResponse; // Store notes in `notes` data property
                    debugLog('Fetched Notes:', notesResponse); // Debugging
                } else {
                    console.error('No notes found');
                }

            } catch (error) {
                // Log the error if fetching family details fails
                console.error('Error fetching stories:', error);
            }

            try {
                const branches = await getPersonBranches(personID);
                if (!Array.isArray(branches)) {
                    throw new TypeError('Invalid branches response');
                }
                this.branches = branches;
                this.branchLoadError = false;
            } catch (e) {
                this.branches = [];
                this.branchLoadError = true;
                console.error('could not load branches', e);
            } finally {
                this.branchLoading = false;
            }

            try {
                const rawParents = await getParents(personID);
                this.printParents = (rawParents || []).map(p => ({
                    name: isLivingPlaceholder(p)
                        ? this.$t('privacy.livingPlaceholder')
                        : p.name || `${p.firstname || ''} ${p.lastname || ''}`.trim() || this.$t('personPage.unknown'),
                    relation: p.sex === 'M' ? this.$t('personPage.parentRelation.father') : p.sex === 'F' ? this.$t('personPage.parentRelation.mother') : this.$t('personPage.parentRelation.parent'),
                    years: formatYears(p.birthdate, p.altbirthdate, p.deathdate, p.burialdate, p.living)
                }));
            } catch (e) {
                console.error('Error fetching parents for print view:', e);
            }

            try {
                // Fetch family details including children
                const familyDetails = await getFamilyDetails(personID);

                // Log the full family details response to check the structure and contents
                debugLog('API Family Details Response:', familyDetails);

                if (familyDetails) {
                    const { families, familyDocuments, familyInfo, showFamilySectionChildCards } = familyDetails;

                    // Save families and familyInfo to the component's state
                    this.families = families;
                    this.familyInfo = familyInfo; // Add this line to set familyInfo
                    this.showFamilySectionChildCards = Boolean(showFamilySectionChildCards);

                    // Log the parsed familyInfo to verify the data
                    debugLog('Parsed familyInfo:', familyInfo);

                    // Map familyInfo to populate marriageDataArray with children data
                    this.marriageDataArray = await Promise.all(familyInfo.map(async info => {
                        const isHusband = this.person.personID === info.husband;
                        const spousePlaceholder = isHusband ? info.wifePlaceholder : info.husbandPlaceholder;
                        const spouseName = isLivingPlaceholder(spousePlaceholder)
                            ? this.$t('privacy.livingPlaceholder')
                            : isHusband ? info.wifeName : info.husbandName;
                        const spouseID = isHusband ? info.wife : info.husband;
                        const childrenData = info.children || [];

                        let spouseYears = '';
                        if (spouseID) {
                            try {
                                const spouseData = await getPersonById(spouseID);
                                spouseYears = formatYears(spouseData.birthdatetr, spouseData.altbirthdatetr, spouseData.deathdatetr, spouseData.burialdatetr, spouseData.living);
                            } catch (e) { /* spouse years unavailable */ }
                        }

                        return {
                            date: info.marrdatetr ? formatDate(info.marrdatetr) : '',
                            place: info.marrplace || '',
                            documents: (familyDocuments[info.familyID] || []).map(document => ({
                                ...document,
                                linkedPersonIDs: normalizeLinkedPersonIDs(document.linkedPersonIDs).length > 0
                                    ? normalizeLinkedPersonIDs(document.linkedPersonIDs)
                                    : [info.husband, info.wife].filter(Boolean)
                            })),
                            spouseName,
                            spouseYears,
                            children: childrenData,
                            familyID: info.familyID,
                            spouseID,
                            marriageNotes: Array.isArray(info.marriageNotes) ? info.marriageNotes : []
                        };
                    }));

                    // Log the final marriageDataArray to verify the transformation
                    debugLog('Final marriageDataArray:', this.marriageDataArray);
                }
            } catch (error) {
                // Log the error if fetching family details fails
                console.error('Error fetching family details:', error);
            }

            // Fetch person profile image
            try {
                const photoData = await getPersonDefaultPhoto(personID);
                if (photoData && photoData.path) {
                    this.person.profileImage = `${process.env.VUE_APP_PHOTO_API_URL}/${encodeURIComponent(photoData.path)}`;
                } else {
                    // Fallback to default based on gender
                    this.person.profileImage = getDefaultPhoto(this.person.sex);
                }
            } catch (error) {
                console.error(`Error fetching profile image for personID ${personID}:`, error);
                // Fallback to default based on gender in case of error
                this.person.profileImage = getDefaultPhoto(this.person.sex);
            }

            // Fetch event-related media
            try {
                const eventMedia = await this.fetchEventMedia(personID);
                debugLog('Event Media:', eventMedia); // Debugging: Check fetched event media
                if (eventMedia && this.person) {
                    this.bioDataGroupsPart1 = this.getEventDetails(this.person, eventMedia); // Populate event details
                }
            } catch (error) {
                console.error('Error fetching event media:', error);
            }

            // Fetch regular media (documents)
            try {
                const personMedia = await getPersonMedia(personID);
                if (personMedia) {
                    this.media = personMedia;
                    // this.$nextTick(() => {
                    //     this.observeMediaCards(); // Observe media cards after they are rendered
                    // });
                } else {
                    console.error('No person media found');
                }
            } catch (error) {
                console.error('Error fetching media data:', error);
            }

            // Fetch photos
            try {
                this.photos = await getPersonPhotos(personID);
                debugLog('Fetched Photos:', this.photos); // Debugging: Check fetched photos

                // Select different random photos for each BioDataCard, or the same if only one is available
                this.selectedPhoto1 = this.getRandomPhotoOrFallback();
                this.selectedPhoto2 = this.getAnotherRandomPhotoOrFallback(this.selectedPhoto1);
            } catch (error) {
                console.error('Error fetching photos:', error);
            }

            // Fetch events
            try {
                const personEvents = await getPersonEvents(personID);
                debugLog('Person Events:', personEvents); // Debugging: Check fetched events
                if (personEvents) {
                    this.bioDataGroupsPart2 = this.getAdditionalEventDetails(personEvents); // Populate additional event details
                }
            } catch (error) {
                console.error('Error fetching events data:', error);
            }

        } catch (error) {
            console.error('Error initializing PersonPage:', error);
        } finally {
            this.loading = false;
            document.body.classList.remove('no-scroll');
            this.signalPrerenderReady();

            if (this.$route.query.print === 'true') {
                this.$nextTick(() => {
                    window.print();
                });
            }
        }
    },
    mounted() {
        document.addEventListener('scroll', this.handleScroll);
    },
    beforeUnmount() {
        document.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        resetPersonScopedState() {
            this.person = null;
            this.families = [];
            this.familyInfo = [];
            this.showFamilySectionChildCards = false;
            this.familyTitle = '';
            this.parents = [];
            this.media = [];
            this.documentationView = 'list';
            this.photos = [];
            this.selectedPhoto1 = '';
            this.selectedPhoto2 = '';
            this.bioDataGroupsPart1 = [];
            this.bioDataGroupsPart2 = [];
            this.marriageDataArray = [];
            this.story = null;
            this.storySlug = null;
            this.familyStories = [];
            this.branches = [];
            this.branchLoadError = false;
            this.branchLoading = true;
            this.notes = [];
            this.printParents = [];
            this.dnaMatches = [];
            this.currentDnaMatch = null;
            this.totalDnaMatches = 0;
            this.showOverlay = false;
            this.showPhotoGalleryOverlay = false;
            this.overlayMedia = null;
            this.overlayMediaArray = [];
            this.currentMediaIndex = 0;
        },
        signalPrerenderReady() {
            if (typeof document === 'undefined') return;

            this.$nextTick(() => {
                document.dispatchEvent(new Event('sejarah-prerender-ready'));
            });
        },
        handlePersonHeaderLoaded() {
            this.loading = false;
            document.body.classList.remove('no-scroll');
        },
        shouldRedirectToLoginForPersonError(error) {
            const status = error?.response?.status;
            return status === 401 || (!this.authToken && status === 404);
        },
        redirectToLogin() {
            this.person = null;
            this.$router.replace({
                path: this.localePath('/login'),
                query: {
                    redirect: this.$route.fullPath,
                },
            });
        },
        advanceDnaMatchQueue() {
            this.currentDnaMatch = this.dnaMatches.shift() || null;
        },
        isPublicMedia(title) {
            if (!title) return false;
            return publicMediaConfig.publicKeywords.some(keyword =>
                title.toLowerCase().includes(keyword.toLowerCase())
            );
        },
        filterAuthorizedMedia(mediaArray) {
            return mediaArray.filter(mediaItem =>
                this.authToken || this.isPublicMedia(mediaItem.description)
            );
        },
        toggleDocumentationView() {
            this.documentationView = this.documentationView === 'cards' ? 'list' : 'cards';
        },
        activateDocument(mediaItem, index) {
            if (this.authToken || this.isPublicMedia(mediaItem.description)) {
                this.openMediaOverlay(mediaItem, index);
                return;
            }

            this.$router.push(this.localePath('/login'));
        },
        splitDocumentDescription(mediaItem) {
            const description = this.toPlainText(mediaItem?.description);
            const owner = this.toPlainText(mediaItem?.owner);
            const separator = ' – ';
            const separatorIndex = description.lastIndexOf(separator);

            if (separatorIndex > 0) {
                const title = description.slice(0, separatorIndex).trim();
                const source = description.slice(separatorIndex + separator.length).trim();

                if (title && source) return { title, source };
            }

            return {
                title: description || this.$t('personPage.mediaOverlay.noTitle'),
                source: owner,
            };
        },
        formatDocumentChangedate(value) {
            const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (!match || match[1] === '0000' || match[2] === '00' || match[3] === '00') return '';

            const year = Number(match[1]);
            const month = Number(match[2]);
            const day = Number(match[3]);
            const date = new Date(Date.UTC(year, month - 1, day));
            if (Number.isNaN(date.getTime())
                || date.getUTCFullYear() !== year
                || date.getUTCMonth() !== month - 1
                || date.getUTCDate() !== day) return '';

            const localeMap = { nl: 'nl-NL', en: 'en-GB', id: 'id-ID' };
            return new Intl.DateTimeFormat(localeMap[this.locale] || localeMap.nl, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                timeZone: 'UTC',
            }).format(date);
        },
        setDocumentTitle() {
            if (this.person && this.person.firstname && this.person.lastname) {
                // Combine firstname and lastname for the title
                document.title = `${this.person.firstname} ${this.person.lastname} | Sejarah Thümann`;
            } else {
                document.title = 'Sejarah Thümann';
            }
        },

        getRandomPhotoOrFallback() {
            if (this.photos.length > 0) {
                const randomIndex = Math.floor(Math.random() * this.photos.length);
                return this.getPhotoPath(this.photos[randomIndex].path); // Use the new method for photos
            } else {
                const randomIndex = Math.floor(Math.random() * this.defaultPhotos.length);
                return this.defaultPhotos[randomIndex];
            }
        },
        getAnotherRandomPhotoOrFallback(excludePhoto) {
            if (this.photos.length === 1) {
                // If there is only one photo available, return the same photo
                return this.getPhotoPath(this.photos[0].path);
            } else if (this.photos.length > 1) {
                let randomIndex;
                let randomPhoto;
                do {
                    randomIndex = Math.floor(Math.random() * this.photos.length);
                    randomPhoto = this.getPhotoPath(this.photos[randomIndex].path);
                } while (randomPhoto === excludePhoto); // Ensure it's different from the first photo
                return randomPhoto;
            } else {
                // If there are no photos, use the fallback method
                return this.getRandomPhotoOrFallback();
            }
        },
        openPhotoGalleryOverlay() {
            debugLog('Opening Photo Gallery Overlay'); // Debugging: Ensure this is called
            this.showPhotoGalleryOverlay = true;
        },
        closePhotoGalleryOverlay() {
            this.showPhotoGalleryOverlay = false;
        },
        handleScroll() {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;

            // Adjust this factor to control rotation speed
            const rotation = scrollTop * 0.2; // Change the multiplier for faster/slower rotation

            const blades = document.getElementById('blades');
            if (blades) {
                blades.style.transform = `rotate(${rotation}deg)`;
            }
        },
        observeMediaCards() {
            debugLog('observeMediaCards method called'); // Debugging: Check if the method is being called

            const mediaCards = document.querySelectorAll('.media_card'); // Correct class selector
            debugLog('Media cards found:', mediaCards.length); // Debugging: Check how many cards are selected

            if (mediaCards.length === 0) {
                debugWarn('No media cards found for observing.'); // Debugging: Check if cards are selected
                return;
            }

            debugLog('Media cards to be observed:', mediaCards); // Debugging: Output selected elements

            const observerOptions = {
                root: null, // Use the viewport as the root
                rootMargin: '0px', // No margin around the root
                threshold: 0.5 // Trigger when 10% of the card is visible
            };

            const observer = new IntersectionObserver((entries, observer) => {
                entries.forEach((entry, index) => {
                    if (entry.isIntersecting) {
                        // Add the 'visible' class with a delay based on the index
                        setTimeout(() => {
                            entry.target.classList.add('visible'); // Add the 'visible' class when the card enters the viewport
                        }, index * 100); // Delay of 200ms between each card's animation

                        observer.unobserve(entry.target); // Stop observing the card after it becomes visible
                    }
                });
            }, observerOptions);

            mediaCards.forEach((card) => {
                observer.observe(card); // Start observing each card
            });
        },
        getMediaForEvent(mediaArray, eventID) {
            return mediaArray
                .filter(media => media.eventID === eventID)
                .map(media => {
                    debugLog('Media path:', media.path); // Log the path value
                    debugLog('Media thumbpath:', media.thumbpath); // Log the thumbpath value
                    return {
                        mediaID: media.mediaID,
                        path: media.path,  // Only use raw path
                        thumbpath: media.thumbpath,  // Only use raw thumbpath
                        description: media.description,
                        owner: media.owner,
                        linkedPersonIDs: normalizeLinkedPersonIDs(media.linkedPersonIDs)
                    };
                });
        },
        getAdditionalEventDetails(events) {
            const eventGroups = {
                OCCU: { title: this.$t('personPage.events.occupation'), eventID: 'OCCU', items: [] },
                EDUC: { title: this.$t('personPage.events.education'), eventID: 'EDUC', format: 'table', items: [] },
                ADDR: { title: this.$t('personPage.events.addresses'), eventID: 'ADDR', format: 'table', items: [] },
                IMMI: { title: this.$t('personPage.events.immigration'), eventID: 'IMMI', format: 'table', items: [] },
                NATU: { title: this.$t('personPage.events.naturalization'), eventID: 'NATU', format: 'table', items: [] },
                CHRA: { title: this.$t('personPage.events.adultBaptism'), eventID: 'CHRA', format: 'table', items: [] },
                ALIA: { title: this.$t('personPage.events.alsoKnownAs'), eventID: 'ALIA', format: 'alias', items: [] },
            };

            events.forEach(event => {
                const formattedDate = formatDate(event.eventdatetr);

                let eventText = event.eventplace
                    + (event.eventplace && event.info ? `, ${event.info}` : event.info || '');

                if (event.tag === 'OCCU' && event.info) {
                    // Eerst dubbele enters naar <br><br>, daarna enkele naar <br>
                    eventText = event.info
                        .replace(/\r?\n\r?\n/g, '<br><br>')
                        .replace(/\r?\n/g, '<br>');
                }

                const eventDetail = {
                    date: formattedDate,
                    rawDate: event.eventdatetr || null, // keep raw date for sorting
                    text: eventText
                };

                switch (event.tag) {
                    case 'OCCU':
                        eventGroups.OCCU.items.push({ text: eventDetail.text, isHtml: true, rawDate: eventDetail.rawDate });
                        break;
                    case 'EDUC':
                        eventGroups.EDUC.items.push({ date: eventDetail.date, text: eventDetail.text, rawDate: eventDetail.rawDate });
                        break;
                    case 'ADDR':
                        eventGroups.ADDR.items.push({ date: eventDetail.date, text: eventDetail.text, rawDate: eventDetail.rawDate });
                        break;
                    case 'ALIA':
                        eventGroups.ALIA.items.push({ text: eventDetail.text });
                        break;
                    case 'IMMI':
                        eventGroups.IMMI.items.push({ date: eventDetail.date, text: eventDetail.text, rawDate: eventDetail.rawDate });
                        break;
                    case 'CHRA':
                        eventGroups.CHRA.items.push({ date: eventDetail.date, text: eventDetail.text, rawDate: eventDetail.rawDate });
                        break;
                    case 'NATU':
                        eventGroups.NATU.items.push({ date: eventDetail.date, text: eventDetail.text, rawDate: eventDetail.rawDate });
                        break;
                    default:
                        break;
                }
            });

            // Helper to parse raw date to sortable value; uses existing extractSortableDate()
            const toTime = raw => {
                const d = extractSortableDate(raw);
                return d ? d.getTime() : null;
            };

            // Sort table-format groups (EDUC, ADDR, IMMI, NATU, CHRA) by rawDate ascending.
            Object.values(eventGroups).forEach(group => {
                if (group.format === 'table' && group.items && group.items.length > 0) {
                    group.items.sort((a, b) => {
                        const ta = toTime(a.rawDate);
                        const tb = toTime(b.rawDate);
                        if (ta !== null && tb !== null) return ta - tb; // both have dates
                        if (ta !== null) return -1; // a has date -> before
                        if (tb !== null) return 1;  // b has date -> before
                        return 0; // neither has date -> keep original order
                    });
                }
            });

            // Remove rawDate before returning (avoid unused-var lint by copying and deleting)
            Object.values(eventGroups).forEach(group => {
                if (group.items && group.items.length > 0) {
                    group.items = group.items.map(item => {
                        const copy = { ...item };
                        delete copy.rawDate;
                        return copy;
                    });
                }
            });

            return Object.values(eventGroups).filter(group => group.items.length > 0);
        },

        // formatDate(dateString) {
        //     if (!dateString) return ''; // Return empty string if date is not provided

        //     const [year, month, day] = dateString.split('-');
        //     // debugLog(`Processing date: ${dateString} - Year: ${year}, Month: ${month}, Day: ${day}`);

        //     if (!year || year === '0000') return ''; // Invalid year
        //     if (month === '00' && day === '00') {
        //         return `${year}`; // Only show the year
        //     } else if (month === '00') {
        //         return `${year}`; // Invalid month, only show year
        //     } else if (day === '00') {
        //         const date = new Date(year, month - 1); // JavaScript months are 0-based
        //         const formatter = new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' });
        //         return formatter.format(date);
        //     } else {
        //         const date = new Date(dateString);
        //         const isValidDate = date instanceof Date && !isNaN(date);
        //         if (isValidDate) {
        //             const formatter = new Intl.DateTimeFormat('nl-NL', {
        //                 weekday: 'long',
        //                 day: 'numeric',
        //                 month: 'long',
        //                 year: 'numeric'
        //             });
        //             return formatter.format(date);
        //         } else {
        //             debugWarn(`Invalid date detected: ${dateString}`);
        //             return ''; // Return empty string for invalid date
        //         }
        //     }
        // },
        formatEventDetails(date, place, options = {}) {
            const items = [];
            const { showJavaneseDate = false } = options;

            // Handle the date formatting more robustly
            if (date && date !== '0000-00-00') {
                const formattedDate = formatDate(date);

                // Add formatted date if it exists after processing
                if (formattedDate) {
                    items.push({ icon: 'date.svg', text: formattedDate }); // Use formatted date

                    if (showJavaneseDate) {
                        // Only convert to Javanese if day, month, AND year are known
                        const [year, month, day] = date.split('-');
                        if (year !== '0000' && month !== '00' && day !== '00') {
                            const formattedJavaneseDate = formatJavaneseDateWithWeton(date);
                            if (formattedJavaneseDate) {
                                items.push({
                                    text: `<span style="padding-left: 22px;">${this.$t('personPage.events.javaneseDay')}: ${formattedJavaneseDate}</span>`,
                                    isHtml: true
                                });
                            }
                        }
                    }
                } else {
                    // Handle cases where only the year is provided
                    const yearOnly = date.split('-')[0];
                    if (yearOnly && yearOnly !== '0000') {
                        items.push({ icon: 'date.svg', text: yearOnly }); // Display only the year
                    }
                }
            }

            // Add place if available
            if (place) {
                items.push({ icon: 'location.svg', text: place });
            }

            return items;
        },
        getEventDetails(personData, eventMedia) {
            const events = [
                {
                    title: this.$t('personPage.events.born'),
                    eventID: 'BIRT', // Add eventID here
                    items: this.formatEventDetails(personData.birthdatetr, personData.birthplace, { showJavaneseDate: true }),
                    images: this.getMediaForEvent(eventMedia, 'BIRT')  // Dynamically fetched images for birth
                },
                {
                    title: this.$t('personPage.events.baptized'),
                    eventID: 'CHR', // Add eventID here
                    items: this.formatEventDetails(personData.altbirthdatetr, personData.altbirthplace),
                    images: this.getMediaForEvent(eventMedia, 'CHR')  // Dynamically fetched images for baptism
                },
                {
                    title: this.$t('personPage.events.deceased'),
                    eventID: 'DEAT', // Add eventID here
                    items: this.formatEventDetails(personData.deathdatetr, personData.deathplace),
                    images: this.getMediaForEvent(eventMedia, 'DEAT')  // Dynamically fetched images for death
                },
                {
                    title: this.$t('personPage.events.buried'),
                    eventID: 'BURI', // Add eventID here
                    items: this.formatEventDetails(personData.burialdatetr, personData.burialplace),
                    images: this.getMediaForEvent(eventMedia, 'BURI')  // Dynamically fetched images for burial
                }
            ];

            return events.filter(event => event.items.length > 0 || event.images.length > 0);
        },
        async fetchEventMedia(personID) {
            try {
                const media = await getEventMedia(personID);
                // Debugging: debugLog('Fetched Event Media:', media);
                return media;
            } catch (error) {
                console.error('Error fetching event media:', error);
                return [];
            }
        },
        getThumbnailPath(thumbpath) {
            return getDocumentThumbnailPath(thumbpath);
        },
        getFilePath(path) {
            return getDocumentPath(path);
        },
        getPhotoPath(path) {
            return getPhotoPathUtil(path) || '@/assets/sejarah_thumann1080x781.jpg';
        },
        toPlainText(value) {
            if (value === null || value === undefined) return '';

            return String(value)
                .replace(/<br\s*\/?>/gi, ', ')
                .replace(/<[^>]+>/g, '')
                .replace(/\s+/g, ' ')
                .trim();
        },
        extractYear(dateValue) {
            if (!dateValue) return '';

            const year = String(dateValue).slice(0, 4);
            return /^\d{4}$/.test(year) && year !== '0000' ? year : '';
        },
        formatPersonName(personLike) {
            if (!personLike) return this.$t('personPage.unknown');
            if (typeof personLike === 'string') return this.toPlainText(personLike);
            if (isLivingPlaceholder(personLike)) return this.$t('privacy.livingPlaceholder');

            const first = personLike.firstname || personLike.firstName || '';
            const last = personLike.lastname || personLike.lastName || '';
            const fullName = `${first} ${last}`.trim();

            if (fullName) return this.toPlainText(fullName);

            return this.toPlainText(
                personLike.name || personLike.personName || personLike.displayName || personLike.personID || this.$t('personPage.unknown')
            );
        },
        formatChildrenNames(children) {
            if (!Array.isArray(children) || children.length === 0) return '-';

            return children
                .map(child => this.formatPersonName(child))
                .filter(Boolean)
                .join('; ');
        },
        formatChildrenWithYears(children) {
            if (!Array.isArray(children) || children.length === 0) return '-';

            return children
                .map(child => {
                    const name = this.formatPersonName(child);
                    if (isLivingPlaceholder(child)) return name;
                    const years = formatYears(child.birthdatetr, child.altbirthdatetr, child.deathdatetr, child.burialdatetr, child.living);
                    return years ? `${name} (${years})` : name;
                })
                .filter(Boolean)
                .join('; ');
        },
        getOverlayMedia(mediaItem) {
            return normalizeOverlayMedia(mediaItem, {
                kind: 'document',
                pathResolver: getDocumentPath,
                defaultTitle: this.$t('personPage.mediaOverlay.noTitle'),
                defaultOwner: this.$t('personPage.mediaOverlay.noOwner')
            });
        },
        // eslint-disable-next-line
        openMediaOverlay(mediaItem, index = 0, mediaArray = null) {
            const isMediaAccessible = (media) => this.authToken || this.isPublicMedia(media.description);

            const filteredMediaArray = (mediaArray || this.media).filter(isMediaAccessible);

            if (!isMediaAccessible(mediaItem)) {
                this.$router.push(this.localePath('/login'));
                return;
            }

            const selectedIndexByIdentity = filteredMediaArray.findIndex((media) => {
                if (media.mediaID && mediaItem.mediaID) {
                    return media.mediaID === mediaItem.mediaID;
                }

                return media.path === mediaItem.path || media.description === mediaItem.description;
            });
            const selectedIndex = selectedIndexByIdentity >= 0
                ? selectedIndexByIdentity
                : index;

            if (selectedIndex < 0 || selectedIndex >= filteredMediaArray.length) {
                debugWarn('Media item not found in filtered media array');
                return;
            }

            this.overlayMediaArray = filteredMediaArray;
            this.currentMediaIndex = selectedIndex;
            this.overlayMedia = this.getOverlayMedia(mediaItem);

            this.showOverlay = true;
            debugLog('Opening MediaOverlay:', {
                mediaItem,
                overlayMedia: this.overlayMedia
            });
        },
        handleMediaNavigation(newIndex) {
            if (newIndex >= 0 && newIndex < this.overlayMediaArray.length) {
                const newMedia = this.overlayMediaArray[newIndex];
                this.openMediaOverlay(newMedia, newIndex, this.overlayMediaArray);
            } else {
                console.error('Navigation index out of bounds:', newIndex);
            }
        },
        closeMediaOverlay() {
            this.showOverlay = false;
        }
    },
    computed: {
        documentationPanelId() {
            return `documentation-media-view-${this.person?.personID || 'person'}`;
        },
        documentListItems() {
            return this.media.map((mediaItem, index) => {
                const { title, source } = this.splitDocumentDescription(mediaItem);
                const isAccessible = Boolean(this.authToken || this.isPublicMedia(mediaItem.description));
                const changedate = this.formatDocumentChangedate(mediaItem.changedate);

                return {
                    mediaItem,
                    index,
                    key: mediaItem.mediaID || mediaItem.path || index,
                    title,
                    source,
                    updatedLabel: changedate
                        ? this.$t('personPage.documents.updatedBy', { date: changedate })
                        : '',
                    isAccessible,
                    thumbnail: isAccessible ? this.getThumbnailPath(mediaItem.thumbpath) : '',
                };
            });
        },
        currentDnaMatchIndex() {
            if (!this.currentDnaMatch || this.totalDnaMatches === 0) {
                return 0;
            }

            return this.totalDnaMatches - this.dnaMatches.length;
        },
        personDisplayName() {
            if (!this.person) return 'Onbekend';
            return this.formatPersonName(this.person);
        },
        personLifecycleRange() {
            if (!this.person) return '';

            const birthYear = this.extractYear(this.person.birthdatetr);
            const deathYear = this.extractYear(this.person.deathdatetr);

            if (birthYear && deathYear) return `${birthYear} - ${deathYear}`;
            if (birthYear) return this.$t('personPage.lifecycle.bornIn', { year: birthYear });
            if (deathYear) return this.$t('personPage.lifecycle.diedIn', { year: deathYear });
            return this.$t('personPage.lifecycle.yearsUnknown');
        },
        printGeneratedDate() {
            const localeMap = { nl: 'nl-NL', en: 'en-GB', id: 'id-ID' };
            const bcp47 = localeMap[this.$i18n.locale] || 'nl-NL';
            return new Intl.DateTimeFormat(bcp47, { dateStyle: 'long' }).format(new Date());
        },
        printSourceUrl() {
            if (this.isPrerenderSnapshot) {
                const personID = encodeURIComponent(this.$route.params.id || '');
                return this.locale === 'nl'
                    ? `https://sejarah-thumann.online/person/${personID}`
                    : `https://sejarah-thumann.online/${this.locale}/person/${personID}`;
            }

            if (typeof window !== 'undefined' && window.location) {
                return window.location.href;
            }

            return this.$route?.fullPath
                ? `https://sejarah-thumann.online${this.$route.fullPath}`
                : '';
        },
        printCoreFacts() {
            if (!this.person) return [];

            const rows = [
                { label: this.$t('personPage.coreFacts.personID'), value: this.person.personID },
                { label: this.$t('personPage.coreFacts.gender'), value: this.person.sex === 'M' ? this.$t('personPage.coreFacts.male') : this.person.sex === 'F' ? this.$t('personPage.coreFacts.female') : '' },
                { label: this.$t('personPage.coreFacts.birth'), value: formatDate(this.person.birthdatetr) },
                { label: this.$t('personPage.coreFacts.birthPlace'), value: this.person.birthplace },
                { label: this.$t('personPage.coreFacts.baptism'), value: formatDate(this.person.altbirthdatetr) },
                { label: this.$t('personPage.coreFacts.baptismPlace'), value: this.person.altbirthplace },
                { label: this.$t('personPage.coreFacts.death'), value: formatDate(this.person.deathdatetr) },
                { label: this.$t('personPage.coreFacts.deathPlace'), value: this.person.deathplace },
                { label: this.$t('personPage.coreFacts.burial'), value: formatDate(this.person.burialdatetr) },
                { label: this.$t('personPage.coreFacts.burialPlace'), value: this.person.burialplace },
            ];

            return rows
                .map(row => ({
                    label: row.label,
                    value: this.toPlainText(row.value)
                }))
                .filter(row => row.value);
        },
        printEventRows() {
            const rows = [];

            this.bioDataGroupsPart1.forEach(group => {
                const dateItem = Array.isArray(group.items)
                    ? group.items.find(item => item.icon === 'date.svg')
                    : null;
                const placeItem = Array.isArray(group.items)
                    ? group.items.find(item => item.icon === 'location.svg')
                    : null;

                rows.push({
                    date: this.toPlainText(dateItem?.text),
                    event: this.toPlainText(group.title) || 'Gebeurtenis',
                    place: this.toPlainText(placeItem?.text),
                    details: ''
                });
            });

            this.bioDataGroupsPart2.forEach(group => {
                if (!Array.isArray(group.items)) return;

                group.items.forEach(item => {
                    const isTableLike = group.format === 'table';

                    rows.push({
                        date: this.toPlainText(item.date),
                        event: this.toPlainText(group.title) || 'Gebeurtenis',
                        place: isTableLike ? this.toPlainText(item.text) : '',
                        details: isTableLike ? '' : this.toPlainText(item.text)
                    });
                });
            });

            return rows.filter(row => row.event && (row.date || row.place || row.details));
        },
        printParentRows() {
            return this.printParents || [];
        },
        printFamilyRows() {
            if (!Array.isArray(this.marriageDataArray) || this.marriageDataArray.length === 0) return [];

            return this.marriageDataArray.map(family => ({
                spouse: this.toPlainText(family.spouseName) || 'Onbekend',
                spouseYears: this.toPlainText(family.spouseYears) || '',
                marriageDate: this.toPlainText(family.date) || '-',
                marriagePlace: this.toPlainText(family.place) || '-',
                children: this.formatChildrenWithYears(family.children)
            }));
        },
        printDocumentRows() {
            if (!Array.isArray(this.media) || this.media.length === 0) return [];

            return this.media
                .map(item => this.toPlainText(item.description || item.path))
                .filter(Boolean);
        },
        printNotesRows() {
            if (!Array.isArray(this.notes) || this.notes.length === 0) return [];

            return this.notes
                .map(note => this.toPlainText(note.note || note.noteText || note.text || note.notes || note))
                .filter(Boolean);
        },
        generalNotes() {
            if (!Array.isArray(this.notes) || this.notes.length === 0) return [];

            const excludedEventIDs = ['BURI', 'DEAT', 'BIRT', 'CHR', 'MARR', 'DIV'];
            return this.notes.filter(note => !excludedEventIDs.includes(note?.eventID));
        },
        isPrintView() {
            return this.$route.query.print === 'true';
        },
        isPrerenderSnapshot() {
            return this.$route.query.prerender === '1';
        },
        hasLifeEventData() {
            // Check if bioDataGroupsPart1 contains any of the life events (by eventID)
            const lifeEventIDs = ['BIRT', 'CHR', 'DEAT', 'BURI'];
            return this.bioDataGroupsPart1.some(group => lifeEventIDs.includes(group.eventID) && group.items.length > 0);
        },
        hasRelevantStories() {
            return !!this.story || (this.familyStories && this.familyStories.length > 0);
        },
        bioDataCardTitle1() {
            return this.$t('personPage.sections.overview');
        },
        bioDataCardEyebrow1() {
            return getGenderBasedText('ꦫꦶꦁꦏꦼꦱꦤ꧀', this.person); // Javanese script — locale-neutral
        },
        bioDataCardTitle2() {
            return this.$t('personPage.sections.events');
        },
        computedGender() {
            return this.person ? this.person.sex : ''; // Safely return gender or empty string if person is null
        },
        dynamicLinkLabel() {
            return getGenderBasedText('Bekijk al haar voorouders', this.person); // Pass this.person
        },
        dynamicDescendantsLinkLabel() {
            return getGenderBasedText('Bekijk al haar nakomelingen', this.person); // Pass this.person
        },
        bioDataCardLabel2() {
            return getGenderBasedText('ꦏꦢꦪꦤ꧀', this.person);
        },
        defaultImageSrc() {
            return `${process.env.VUE_APP_STRAPI_UPLOADS_URL}/default_image.jpg`;
        },
        hasDocumentation() {
            return Array.isArray(this.media) && this.media.length > 0;
        },
        hasDescendantGraph() {
            const familyInfo = Array.isArray(this.familyInfo) ? this.familyInfo : [];
            return familyInfo.some(info => Array.isArray(info.children) && info.children.length > 0);
        },
        hasFamilySection() {
            return Array.isArray(this.families) && this.families.length > 0;
        },
        hasEventSectionContent() {
            const lifeEvents = Array.isArray(this.bioDataGroupsPart1) ? this.bioDataGroupsPart1 : [];
            const additionalEvents = Array.isArray(this.bioDataGroupsPart2) ? this.bioDataGroupsPart2 : [];
            const marriages = Array.isArray(this.marriageDataArray) ? this.marriageDataArray : [];

            return lifeEvents.length > 0
                || additionalEvents.length > 0
                || marriages.length > 0
                || this.generalNotes.length > 0
                || this.hasDescendantGraph;
        },
        showEventSection() {
            return this.hasEventSectionContent;
        },
        documentationSectionStyle() {
            return {
                marginTop: '0px'
            };
        },
        kinshipSectionClasses() {
            return {
                'kinshipSection--after-descendants-no-docs': !this.hasDocumentation && this.hasDescendantGraph,
                'kinshipSection--after-empty-content': !this.showEventSection
                    && !this.hasDocumentation
                    && !this.hasFamilySection
            };
        },
        hasVisibleFamilySection() {
            return this.hasFamilySection && this.hasDescendantGraph;
        },
        sectionNavigationReady() {
            return !Object.values(this.sectionLoading || {}).some(Boolean);
        },
        navigablePersonSections() {
            return [
                {
                    id: 'bioDataSection',
                    label: this.$t('personPage.sections.overview'),
                    visible: this.hasLifeEventData
                },
                {
                    id: 'parentSection',
                    label: this.$t('parentSection.sectionTitle'),
                    visible: true
                },
                {
                    id: 'eventSection',
                    label: this.$t('personPage.sections.events'),
                    visible: this.showEventSection
                },
                {
                    id: 'familySection',
                    label: this.$t('familySection.sectionTitle'),
                    visible: this.hasVisibleFamilySection
                },
                {
                    id: 'documentationSection',
                    label: this.$t('personPage.sections.documents'),
                    visible: this.hasDocumentation
                },
                {
                    id: 'kinshipSection',
                    label: this.$t('personPage.sections.kinship'),
                    visible: true
                }
            ]
                .filter(section => section.visible)
                .map(({ id, label }) => ({ id, label }));
        }
    }
};
</script>

<style scoped>
.print-dossier {
    display: none;
}

.print-dossier--screen {
    display: block;
    max-width: 1080px;
    margin: 56px auto;
    padding: 0 20px 56px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 16px;
    line-height: 1.4;
}

.print-dossier--screen .print-eyebrow,
.print-dossier--screen .print-header h1,
.print-dossier--screen .print-subtitle,
.print-dossier--screen h2,
.print-dossier--screen th,
.print-dossier--screen td,
.print-dossier--screen .print-footer,
.print-dossier--screen .print-footer p {
    color: #000 !important;
}

.decoration svg {
    overflow: visible;
}

#blades {
    transform-origin: 50.5% 40%;
    transition: transform .9s cubic-bezier(.49, 1.42, .25, 1.53);
}

h1 {
    margin-bottom: 24px;
}

h3 {
    margin-top: 32px;
}

p {
    margin-bottom: 12px;
    /* font-size: 18px; */
}

.MediaCardGroup_container {
    padding: var(--page-padding);
    /* background: pink; */
}

.DocumentationSection_heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
}

.DocumentationSection_view_toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding: 9px 14px;
    border: 1px solid rgba(29, 29, 29, 0.25);
    border-radius: var(--border-radius-pill);
    background: rgba(29, 29, 29, 0.05);
    color: var(--brand-text-dark);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.DocumentationSection_view_toggle:hover {
    border-color: rgba(29, 29, 29, 0.5);
    background: rgba(29, 29, 29, 0.1);
}

.DocumentationSection_view_toggle:focus-visible {
    outline: 2px solid var(--brand-color-1);
    outline-offset: 2px;
}

#documentationSection .MediaCardGroup_container--after-events-no-descendants {
    padding-top: 0;
}

.buffer {
    padding-top: 144px;
}

.MediaCards {
    display: flex;
    align-items: flex-start;
    align-content: flex-start;
    gap: 20px;
    align-self: stretch;
    flex-wrap: wrap;
    margin-top: 32px;
}

.media_card {
    width: calc(20% - 16px);
}

.DocumentationList {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 32px 0 0;
    padding: 0;
    list-style: none;
}

.DocumentationList_item {
    width: 100%;
}

.DocumentationList_button {
    display: flex;
    align-items: stretch;
    width: 100%;
    min-height: 112px;
    padding: 0;
    overflow: hidden;
    border: 1px solid rgba(29, 29, 29, 0.2);
    border-radius: var(--border-radius-md);
    background: rgba(255, 255, 255, 0.35);
    color: var(--brand-text-dark);
    font: inherit;
    text-align: left;
    cursor: pointer;
    box-shadow: 0 0 0 rgba(29, 29, 29, 0);
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.DocumentationList_button:hover {
    border-color: rgba(29, 29, 29, 0.45);
    background: rgba(255, 255, 255, 0.65);
    box-shadow: var(--shadow-card-sm);
}

.DocumentationList_button:hover .DocumentationList_title {
    text-decoration: underline;
}

.DocumentationList_button:focus-visible {
    outline: 2px solid var(--brand-color-1);
    outline-offset: 2px;
}

.DocumentationList_thumbnail {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 150px;
    width: 150px;
    min-height: 112px;
    overflow: hidden;
    border-right: 1px solid rgba(29, 29, 29, 0.2);
    background: var(--brand-bg-2);
}

.DocumentationList_thumbnail_image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.DocumentationList_button--guest .DocumentationList_thumbnail {
    background: rgba(29, 29, 29, 0.9);
}

.DocumentationList_lock {
    width: 24px;
    height: 24px;
    transition: transform 0.15s ease;
}

.DocumentationList_button--guest:hover .DocumentationList_lock {
    transform: scale(1.15);
}

.DocumentationList_copy {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    padding: 20px 24px;
}

.DocumentationList_title {
    display: -webkit-box;
    overflow: hidden;
    color: var(--brand-text-dark);
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
}

.DocumentationList_source {
    margin-top: 8px;
    color: var(--brand-text-dark);
    font-size: 14px;
    line-height: 1.4;
    opacity: 0.65;
}

.DocumentationList_updated {
    margin-top: 10px;
    color: var(--brand-text-dark);
    font-size: 12px;
    line-height: 1.4;
    opacity: 0.5;
}

.StoryCard_wrapper {
    padding-top: 0;
    margin-top: -72px;
}

#kinshipSection {
    background: url('@/assets/noise128x128.png'), linear-gradient(to bottom, var(--brand-bg-2) 50%, var(--brand-bg-4) 50%);
}

#kinshipSection.kinshipSection--after-descendants-no-docs,
#kinshipSection.kinshipSection--after-empty-content {
    background: url('@/assets/noise128x128.png'), linear-gradient(to bottom, var(--brand-bg-8) 50%, var(--brand-bg-4) 50%);
}

.post_suggestions {
    width: 100%;
    background: var(--brand-bg-4);
}

.post_suggestions_container {
    max-width: 1280px;
    padding: 144px 0;
    margin-left: auto;
    margin-right: auto;
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

::v-deep(.suggested_blog_post p) {
    color: var(--brand-text-light) !important;
}

@media (max-width: 860px) {
    .MediaCardGroup_container {
        padding: var(--page-padding-mobile);
    }

    .DocumentationSection_heading {
        align-items: flex-start;
        flex-direction: column;
    }

    .DocumentationSection_view_toggle {
        align-self: flex-end;
    }

    .MediaCards {
        flex-direction: column;
    }

    .media_card {
        width: 100%;
        flex-direction: row;
    }

    ::v-deep(.media_card_image) {
        padding-bottom: 20%;
        padding-left: 28%;
        align-self: stretch;
        width: 0;
    }

    .DocumentationList_button {
        min-height: 96px;
    }

    .DocumentationList_thumbnail {
        flex-basis: 112px;
        width: 112px;
        min-height: 96px;
    }

    .DocumentationList_copy {
        padding: 16px;
    }

    .decoration svg {
        height: 48px;
    }

    .PersonHeader_years {
        font-size: 18px;
    }
}

.dna-notifications-container {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
}

.dna-notifications-container :deep(> *) {
    pointer-events: auto;
}

@media (max-width: 768px) {
    .dna-notifications-container {
        bottom: 16px;
        right: 16px;
    }
}

@media (max-width: 480px) {
    .dna-notifications-container {
        bottom: 12px;
        right: 12px;
        left: 12px;
        right: 12px;
    }
}

@media print {
    @page {
        size: A4;
        margin: 14mm;
    }

    .person-page-screen {
        display: none !important;
    }

    .print-dossier {
        display: block;
        color: #111;
        background: #fff;
        font-family: Georgia, 'Times New Roman', serif;
        font-size: 11pt;
        line-height: 1.35;
    }

    .print-header {
        margin-bottom: 14px;
        border-bottom: 1px solid #000;
        padding-bottom: 8px;
    }

    .print-eyebrow {
        margin: 0 0 4px;
        font-size: 9pt;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #000;
    }

    .print-header h1 {
        margin: 0;
        font-size: 21pt;
        line-height: 1.15;
        color: #000;
    }

    .print-subtitle {
        margin: 6px 0 0;
        font-size: 11pt;
        color: #000;
    }

    .print-section {
        margin-top: 14px;
        page-break-inside: avoid;
    }

    .print-section h2 {
        margin: 0 0 6px;
        font-size: 12pt;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    .print-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
    }

    .print-table th,
    .print-table td {
        border: 1px solid #000;
        padding: 6px;
        vertical-align: top;
        text-align: left;
        word-break: break-word;
    }

    .print-table th {
        font-weight: 700;
        background: #f1f1f1;
    }

    .print-table--timeline th:nth-child(1),
    .print-table--timeline td:nth-child(1) {
        width: 20%;
    }

    .print-table--timeline th:nth-child(2),
    .print-table--timeline td:nth-child(2) {
        width: 20%;
    }

    .print-table--timeline th:nth-child(3),
    .print-table--timeline td:nth-child(3) {
        width: 30%;
    }

    .print-table--timeline th:nth-child(4),
    .print-table--timeline td:nth-child(4) {
        width: 30%;
    }

    .print-footer {
        margin-top: 14px;
        border-top: 1px solid #000;
        padding-top: 8px;
        font-size: 9pt;
        color: #000;
    }

    .print-footer p {
        margin: 0;
    }
}
</style>
